import { useTranslation } from 'react-i18next'
import { Column } from 'primereact/column'
import { useNavigateWithOrigin } from '../../../app/nav/originNav'
import Button from '../../../components/ui/Button'
import DataTable from '../../../components/ui/DataTable'
import EmptyState from '../../../components/ui/EmptyState'
import SectionCard from '../../../components/ui/SectionCard'
import { formatDate } from '../../../lib/datetime'
import DashboardAssigneeCell from './DashboardAssigneeCell'
import { BUCKET_TONE, STATUS_TONE_BADGE_CLASS } from '../../actions/types'
// Rozet stilleri (`.act-badge*`) aksiyon özelliğinin yaprağındadır; pano onları KULLANIR, kopyalamaz →
// bağımlılık açıkça bildirilir (aynı desen: `MinuteItemsEditor` de bu dosyayı içeri alır).
import '../../actions/actions.css'
import type { TableSort } from '../../../lib/useTableSort'
import { ACTIONS_PAGE_SIZE } from '../hooks/useDashboardActions'
import { playClickSound } from '../../../lib/sound'
import type { ActionSelectionKeys, DashboardActionRow } from '../types'
import { useCurrentUser } from '../../../auth/useCurrentUser'

interface Props {
  rows: DashboardActionRow[]
  loading: boolean
  /** Toplam kayıt (sunucudan) — sayfalayıcı bunu gösterir. */
  total: number
  /** 0 tabanlı sayfa numarası. */
  page: number
  onPageChange: (page: number) => void
  /** Kolon sıralaması (`useTableSort`) — sunucu-taraflı; varsayılanı termin ↑'dir. */
  sorting: TableSort
  /** Seçili kova/ana durumlar (çoklu); boş dizi → seçim yok (tüm statüler listelenir). */
  selectedSelection: ActionSelectionKeys
  /**
   * "Görevleri Önceliklendir" açık mı? Kararı çağıran verir (§K6/SC-009: kapalı statülerde pasif);
   * kart ikinci bir kural yorumlamaz.
   */
  remindable: boolean
  /**
   * Seçili kovaların/grupların sunucudan gelen görünen adları (donut legend'ıyla aynı kaynak), **sunucu
   * sırasında**. Kart onları yalnız açıklama satırına yazar; sıralamaz, kısaltmaz ve ad üretmez.
   */
  bucketNames: readonly string[]
  /**
   * Seçim panonun **açılış varsayılanından** mı geldi (kullanıcı tıklamadı)? Yalnız açıklama satırının
   * cümlesini belirler — 2026-09-11'den beri pano "Aksiyon Bekleniyor" seçili açılır.
   */
  openedWithDefault: boolean
  /**
   * "Yönetici Bazlı …" grafiğinde seçilen kişinin adı; seçim yoksa `null` (2026-09-11). Yalnız
   * açıklama satırını yazmak içindir — süzgecin kendisi sorguya {@code employeeIds} olarak girer.
   */
  personName: string | null
  /**
   * Tek satır hatırlatması. Bu ve {@link onRemindAll} verilmezse "Önceliklendir" kolonu ve "Görevleri
   * Önceliklendir" butonu **hiç çizilmez** (`DASHBOARD_PRIORITIZE` yetkisi yoksa — {@code useCanPrioritize}).
   */
  onRemind?: (row: DashboardActionRow) => void
  /**
   * "Görevleri Önceliklendir" butonu. 2026-10-21'den beri doğrudan göndermez: çağıran, hedefin (kalan
   * süre / gecikenler) seçildiği pencereyi açar — kart pencereyi bilmez.
   */
  onRemindAll?: () => void
  /** Bir hatırlatma isteği sürüyor mu? (butonlar pasifleşir — çift gönderim olmasın) */
  reminding: boolean
}

/**
 * Yönetici Raporu'nun **alt tablosu** — "Aksiyon Beklenen Maddeler" (079; 077 §K3/§K4'ün revizyonu).
 *
 * <p><b>Bir özet değil, çalışma listesidir:</b> donut'ta seçilen ana duruma (ya da sütun grafiğinde
 * seçilen kırılıma) göre daralır, sunucu-taraflı **10'ar** sayfalanır, **her kolondan** sıralanabilir ve
 * her satırdan hatırlatma/önceliklendirme gönderilebilir.
 *
 * <p><b>079'da değişenler:</b>
 *
 * <ul>
 *   <li><b>Başlık sabitlendi</b>: seçime göre değişen başlık yerine tek ad ("Aksiyon Beklenen Maddeler");
 *       hangi statünün seçili olduğu başlığın altındaki açıklama satırında yazar. Başlık kayan bir metin
 *       olduğunda kullanıcı bölümü adıyla arayamıyordu.
 *   <li><b>"Önem Derecesi" kolonu kaldırıldı</b> (mockup'ta yok); önem bilgisi Aksiyon Detay'da durur.
 *   <li><b>Sıralama</b>: dört veri kolonu da sıralanabilir, varsayılan <b>termin ↑</b>. Sıralama
 *       sunucudadır — 10'luk sayfa yalnız kendi içinde sıralansaydı "en yakın termin" listenin başında
 *       değil, o sayfanın başında olurdu.
 * </ul>
 *
 * <p><b>Boş hücre kuralı</b> (053/FR-011c): değeri olmayan alan <b>boş</b> bırakılır, {@code -}/{@code —}
 * yazılmaz. "Hatırlat/Önceliklendir" butonundaki tıklama satıra sızmaz ({@code stopPropagation}) — aksi
 * hâlde her hatırlatma kullanıcıyı aynı anda detay sayfasına atardı.
 */
export default function DashboardActionsCard({
  rows,
  loading,
  total,
  page,
  onPageChange,
  sorting,
  selectedSelection,
  remindable,
  bucketNames,
  openedWithDefault,
  personName,
  onRemind,
  onRemindAll,
  reminding,
}: Readonly<Props>) {
  const { t } = useTranslation()
  /** 129 — IT Admin maskesinde başlık/açıklama `***` gelir; sıralama oku çizilmez. */
  const contentMasked = useCurrentUser().data?.actionContentMasked === true
  // Detaya giderken anasayfa origin olarak taşınır → Aksiyon Detay'da "Geri" anasayfaya döner.
  const navigate = useNavigateWithOrigin()

  const filtered = selectedSelection.length > 0

  /**
   * Tablonun altındaki tek satırlık açıklama: kümenin **neden** bu kadar olduğunu yazıyla söyler.
   * İki süzgeç birbirinden bağımsızdır (statü · kişi) → dört hâl vardır; kişi seçiliyken metin onu da
   * anmalıdır, aksi hâlde kullanıcı daralmış listeyi yalnız statüye bağlar. Statü **açılış
   * varsayılanından** geldiyse cümle "tıklanarak filtrelendi" DEMEZ — kullanıcı tıklamamıştır.
   *
   * <p><b>Çoklu seçimde adlar tek tek yazılır</b> ("… 'A', 'B' statülerindeki …"), sayıyla özetlenmez
   * ("2 statü"): cümlenin tek işi kümenin sınırını söylemektir ve sayı, kullanıcıyı hangi statülerin
   * seçili olduğunu grafikten saymaya zorlardı. Çoğul için AYRI anahtar gerekir — Türkçede ek
   * ("statüsüne" → "statülerindeki") tekil/çoğulda farklıdır ve i18next'in `count` çoğullaması cümlenin
   * ortasındaki bu eki çözmez.
   */
  const note = () => {
    const names = filtered ? bucketNames : []
    const many = names.length > 1
    // Tek adı i18n dizgesi kendi tırnakları içine alır; ÇOKLU listede tırnak buradan gelir — aksi hâlde
    // parantezli adlar ("Tamamlandı (Gecikmiş)") virgülle birleşince adların sınırı okunamaz olurdu.
    const status = many ? names.map((n) => `'${n}'`).join(', ') : (names[0] ?? null)
    if (status == null) {
      return personName != null
        ? t('home.actions.personNote', { person: personName, count: total })
        : t('home.actions.defaultNote', { count: total })
    }
    if (personName != null) {
      return t(many ? 'home.actions.personStatusMultiNote' : 'home.actions.personStatusNote', {
        person: personName,
        name: status,
        count: total,
      })
    }
    // Açılış varsayılanı daima TEK ana durumdur (`DEFAULT_OPEN_SELECTION`) → çoğul karşılığı yoktur.
    if (openedWithDefault) return t('home.actions.openDefaultNote', { name: status, count: total })
    return t(many ? 'home.actions.filteredMultiNote' : 'home.actions.filteredNote', {
      name: status,
      count: total,
    })
  }

  const header = onRemindAll && (
    <Button
      type="button"
      outlined
      className="home-remind-all"
      icon="pi pi-bell"
      label={t('home.remind.all')}
      disabled={!remindable || reminding || total === 0}
      // Ses burada ÇALMAZ: buton yalnız pencereyi açar; gönderim sesi pencerenin "Bildirim Gönder"
      // butonundadır (`PrioritizeTasksDialog`) — kullanıcı isteği 2026-10-26.
      onClick={onRemindAll}
    />
  )

  return (
    <SectionCard title={t('home.actions.title')} hint={header} className="home-crit">
      <p className="home-actions-note">{note()}</p>

      <DataTable
        className="home-actions-table"
        value={rows}
        loading={loading}
        lazy
        paginator
        first={page * ACTIONS_PAGE_SIZE}
        rows={ACTIONS_PAGE_SIZE}
        totalRecords={total}
        // Sayfa boyu sabittir (077 §K4: "10'ar 10'ar") → satır/sayfa seçici gösterilmez.
        rowsPerPageOptions={undefined}
        onPage={(e) => onPageChange(Math.floor((e.first ?? 0) / ACTIONS_PAGE_SIZE))}
        sortField={sorting.sortField}
        sortOrder={sorting.sortOrder}
        onSort={sorting.onSort}
        // Sıralama daima bir kolondadır (varsayılan termin ↑): "sıralamasız" hâl, kullanıcının
        // bilerek seçebileceği bir durum değil, yalnız kafa karıştıran üçüncü bir tıklama olurdu.
        removableSort={false}
        onRowClick={(e) => navigate(`/minutes/actions/${(e.data as DashboardActionRow).id}`)}
        rowClassName={() => 'mtg-row-clickable'}
        emptyMessage={
          <EmptyState
            icon="pi pi-check-square"
            title={t('home.actions.emptyTitle')}
            description={t('home.actions.emptyDesc')}
          />
        }
      >
        <Column
          field="assignee"
          sortable
          header={t('home.actions.columns.assignee')}
          body={(row: DashboardActionRow) => (
            <DashboardAssigneeCell assignees={row.assignees} />
          )}
        />
        {/* Başlık hücresi SARAR — uzun bir başlık tabloyu yana kaydırmasın (kullanıcı geri bildirimi
            2026-08-07). `tk-cell-wrap` global değiştiricidir (aynı sorunu üç ekran yaşıyor);
            `home-actions-name` yalnız bu kolonun ölçülerini (taban genişlik, satır aralığı) taşır. */}
        <Column
          field="name"
          sortable={!contentMasked}
          header={t('home.actions.columns.name')}
          bodyClassName="tk-cell-strong tk-cell-wrap home-actions-name"
        />
        <Column
          field="deadline"
          sortable
          header={t('home.actions.columns.deadline')}
          body={(row: DashboardActionRow) => (row.deadline ? formatDate(row.deadline) : '')}
        />
        {/* Durum (kullanıcı isteği 2026-09-11). Ad ve renk SUNUCUNUN kovasından gelir: metin
            `statusName` (mesaj paketi), ton `BUCKET_TONE` (donut ile aynı kaynak) → grafikte mavi
            gördüğü durumu tabloda da mavi görür. Adı olmayan satır hücreyi BOŞ bırakır (053/FR-011c).
            Sıralama sunucudadır: `status` alanı ana durumu Aksiyon Bekleniyor → Tamamlandı → Kapatıldı
            sırasında verir; grup içinde satırlar termin ↑ düzeninde kalır. */}
        <Column
          field="status"
          sortable
          header={t('home.actions.columns.status')}
          body={(row: DashboardActionRow) =>
            row.statusName ? (
              <span className={`act-badge ${STATUS_TONE_BADGE_CLASS[BUCKET_TONE[row.group ?? row.bucket] ?? 'neutral']}`}>
                {row.statusName}
              </span>
            ) : (
              ''
            )
          }
        />
        <Column
          field="overdueDays"
          sortable
          header={t('home.actions.columns.overdue')}
          body={(row: DashboardActionRow) =>
            row.overdueDays == null ? (
              ''
            ) : (
              <span className="home-crit-late">
                {t('home.critical.days', { count: row.overdueDays })}
              </span>
            )
          }
        />
        {onRemind && (
          <Column
            header={t('home.actions.columns.remind')}
            body={(row: DashboardActionRow) => (
              <Button
                type="button"
                outlined
                className="home-remind"
                icon="pi pi-bell"
                label={t('home.remind.one')}
                disabled={!remindable || reminding}
                onClick={(e) => {
                  // Satır tıklaması detaya götürür; hatırlatma onu tetiklememeli.
                  e.stopPropagation()
                  playClickSound()
                  onRemind(row)
                }}
              />
            )}
          />
        )}
      </DataTable>
    </SectionCard>
  )
}
